import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { faqItems } from '@/lib/faqContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const artistGuides = [
  {
    keywords: ['flute', 'bansuri', 'instrument', 'music teacher', 'train students', 'student', 'class'],
    title: 'For flute and music artists',
    answer:
      'Create a strong artist profile with your city, teaching style, YouTube or Instagram performance links, and mention if you offer online classes, offline classes, workshops, beginner batches, or advanced training. Add short videos showing tone, raga basics, film songs, devotional pieces, and teaching clips so students and collaborators can trust your work.\n\nHindi: अपनी सिटी, सीखाने का तरीका, वीडियो लिंक और ऑनलाइन/ऑफलाइन क्लास की जानकारी प्रोफाइल में साफ लिखें.',
  },
  {
    keywords: ['singer', 'vocal', 'playback', 'ghazal', 'sufi', 'bhajan', 'rapper'],
    title: 'For singers and vocal artists',
    answer:
      'Add your best YouTube, Instagram, or Spotify links under portfolio. Mention your genre, city, live-show experience, language comfort, and whether you are available for recordings, events, devotional programs, jingles, or online vocal training.\n\nHindi: अपनी गायकी की शैली, शहर, अनुभव और अच्छे ऑडियो/वीडियो लिंक जरूर जोड़ें.',
  },
  {
    keywords: ['dancer', 'dance', 'kathak', 'bharatanatyam', 'hip hop', 'choreographer'],
    title: 'For dancers and choreographers',
    answer:
      'Upload a clear profile photo and add performance videos. Mention your dance form, city, training background, stage experience, group/solo availability, workshop availability, and whether you teach students online or offline.\n\nHindi: अपना डांस फॉर्म, शहर, ट्रेनिंग और परफॉर्मेंस वीडियो साफ-साफ जोड़ें.',
  },
  {
    keywords: ['actor', 'acting', 'theatre', 'voice over', 'voice-over', 'audition'],
    title: 'For actors and voice artists',
    answer:
      'Keep your profile focused on casting-ready details: city, acting category, languages, theatre or screen experience, voice samples, showreel links, and a short bio. Collaborators can contact verified artists through profile enquiry and PMB.\n\nHindi: ऑडिशन और कास्टिंग के लिए अपनी भाषा, शहर, अनुभव और शोरील लिंक जोड़ें.',
  },
  {
    keywords: ['influencer', 'creator', 'youtube creator', 'instagram creator', 'reels', 'brand'],
    title: 'For influencers and digital creators',
    answer:
      'Add your platform links, niche, city, audience style, content categories, and brand collaboration availability. A clean profile helps brands and collaborators understand what kind of campaign or creative work suits you.',
  },
  {
    keywords: ['writer', 'poet', 'script', 'dialogue', 'lyricist', 'content writer'],
    title: 'For writers and lyricists',
    answer:
      'Use your bio to explain your writing style, languages, genres, and project types. Add portfolio links such as published work, YouTube songs, Spotify tracks, Instagram writing samples, or a professional page.',
  },
  {
    keywords: ['folk', 'traditional', 'regional', 'bhajan', 'qawwali', 'lavani', 'bihu', 'garba', 'devotional'],
    title: 'For folk, devotional, and regional artists',
    answer:
      'Mention your region, language, traditional form, group size if any, instruments used, event availability, and video proof of past performances. Folk and regional artists should write city/state clearly so local organizers can discover them.\n\nHindi: अपनी लोक कला, क्षेत्र, भाषा, शहर/राज्य और कार्यक्रम के वीडियो लिंक जरूर जोड़ें.',
  },
  {
    keywords: ['photographer', 'video editor', 'cinematographer', 'film maker', 'filmmaker', 'graphic designer', 'animator'],
    title: 'For visual, film, and digital media artists',
    answer:
      'Add a portfolio link that shows your best work, mention your tools, city, project type, turnaround style, and whether you work remotely or on location. A short, specific bio helps production houses and brands understand your fit.',
  },
];

const quickPrompts = [
  'How do I register as an artist?',
  'How do collaborators contact artists?',
  'Why is admin verification needed?',
  'How do I add YouTube, Spotify, or Instagram?',
  'I am a flute artist. How can I train students?',
  'How do I report unsafe content?',
];

const intentGuides = [
  {
    keywords: ['register', 'signup', 'sign up', 'join as artist', 'artist join', 'create profile'],
    title: 'Artist registration',
    answer:
      'Click Artist Join, choose Email + Password or Mobile OTP, then create your profile. Add name, email, city, category, bio, image, and at least one portfolio link: Instagram, YouTube, or Spotify. Admin verification is required before public listing.',
    source: 'Registration help',
  },
  {
    keywords: ['collaborator join', 'join as collaborator', 'production house', 'client signup', 'organizer'],
    title: 'Collaborator registration',
    answer:
      'Click Collaborator Join, create an account, and add organization name, type, city, bio, and contact details. After verification, collaborators can browse artist profiles and send enquiries through PMB.',
    source: 'Registration help',
  },
  {
    keywords: ['portfolio', 'youtube', 'spotify', 'instagram', 'link', 'profile link'],
    title: 'Portfolio links',
    answer:
      'Artists should add at least one real portfolio link: Instagram, YouTube, or Spotify. These links help admin verify the profile and help collaborators understand the artist work before contacting.',
    source: 'Profile help',
  },
  {
    keywords: ['verified', 'verification', 'admin verify', 'not showing', 'public listing'],
    title: 'Admin verification',
    answer:
      'Profiles are reviewed before public listing. This keeps KalaManch cleaner and safer. If your profile is complete but not visible, wait for admin review or contact support with your registered email.',
    source: 'Verification help',
  },
  {
    keywords: ['pmb', 'message', 'enquiry', 'inbox', 'contact artist', 'contact collaborator'],
    title: 'Private Message Board',
    answer:
      'PMB keeps artist-collaborator conversations in one safe place. Collaborators can send enquiries to artists, and logged-in artists can message collaborators from collaborator profiles.',
    source: 'PMB help',
  },
];

const fallbackAnswer =
  'I can help with KalaManch registration, profiles, verification, portfolio links, enquiries, PMB, media review, and basic artist profile guidance. I do not give legal, payment, contract, or guaranteed-work advice. For urgent, unsafe, account-specific, or detailed support, please contact the KalaManch team from the Contact page.\n\nHindi: जरूरी या खास समस्या के लिए Contact page से KalaManch टीम को संदेश भेजें.';

const normalize = (value = '') => value.toLowerCase().replace(/[^a-z0-9+@\s-]/g, ' ');

const buildSupportUrl = (question = '') => {
  const params = new URLSearchParams({
    subject: 'KalaManch Assistant Help',
    message: question
      ? `Question asked in KalaManch Assistant:\n\n${question}\n\nPlease help me with this.`
      : 'I need help from KalaManch support.',
  });
  return `/contact?${params.toString()}`;
};

const scoreQuestion = (query, item) => {
  const normalizedQuery = normalize(query);
  const normalizedQuestion = normalize(item.question);
  const normalizedAnswer = normalize(item.answer);
  return normalizedQuery
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .reduce((score, word) => score + (normalizedQuestion.includes(word) ? 3 : 0) + (normalizedAnswer.includes(word) ? 1 : 0), 0);
};

const getAssistantReply = (query) => {
  const normalizedQuery = normalize(query);

  const artistGuide = artistGuides.find((guide) =>
    guide.keywords.some((keyword) => normalizedQuery.includes(keyword)),
  );

  if (artistGuide) {
    return {
      title: artistGuide.title,
      body: artistGuide.answer,
      source: 'Artist guidance',
    };
  }

  const intentGuide = intentGuides.find((guide) =>
    guide.keywords.some((keyword) => normalizedQuery.includes(keyword)),
  );

  if (intentGuide) {
    return intentGuide;
  }

  const rankedFaqs = faqItems
    .map((item) => ({ ...item, score: scoreQuestion(query, item) }))
    .sort((a, b) => b.score - a.score);

  if (rankedFaqs[0]?.score > 0) {
    return {
      title: rankedFaqs[0].question,
      body: rankedFaqs[0].answer,
      source: 'FAQ',
    };
  }

  return {
    title: 'KalaManch support',
    body: fallbackAnswer,
    source: 'Support',
  };
};

const KalaManchAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [accountUiOpen, setAccountUiOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [lastQuestion, setLastQuestion] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      title: 'Namaste, I am the KalaManch Help Assistant.',
      body: 'Ask me about artist registration, collaborator signup, verification, portfolio links, PMB messages, media review, or improving your artist profile.\n\nHindi: कलाकार प्रोफाइल, वेरिफिकेशन, पोर्टफोलियो और मैसेजिंग से जुड़े सवाल पूछ सकते हैं.',
      source: 'Welcome',
    },
  ]);

  const suggestedPrompts = useMemo(() => quickPrompts, []);

  useEffect(() => {
    const handleAccountUiChanged = (event) => {
      const nextOpen = Boolean(event?.detail?.open);
      setAccountUiOpen(nextOpen);
      if (nextOpen) setIsOpen(false);
    };
    window.addEventListener('kalamanchAccountUiChanged', handleAccountUiChanged);
    return () => window.removeEventListener('kalamanchAccountUiChanged', handleAccountUiChanged);
  }, []);

  const askAssistant = async (nextQuery) => {
    const cleanQuery = nextQuery.trim();
    if (!cleanQuery || isThinking) return;

    const userMessage = { role: 'user', body: cleanQuery };
    setLastQuestion(cleanQuery);
    setMessages((current) => [...current, userMessage]);
    setQuery('');
    setIsThinking(true);

    try {
      const conversation = [...messages, userMessage]
        .slice(-10)
        .map((message) => ({
          role: message.role === 'assistant' ? 'assistant' : 'user',
          content: [message.title, message.body].filter(Boolean).join('\n\n'),
        }))
        .filter((message) => message.content.trim());
      const response = await axios.post(
        `${API}/ask-kalamanch`,
        { messages: conversation },
        { withCredentials: true },
      );
      const answer = response.data?.answer || 'I could not get a clean answer. Please try again or contact support.';
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          title: 'Ask KalaManch',
          body: answer,
          source: response.data?.support_ticket ? 'Support ticket created' : 'KalaManch AI',
        },
      ]);
    } catch (error) {
      const reply = getAssistantReply(cleanQuery);
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          ...reply,
          body: `${reply.body}\n\nLive Ask KalaManch is temporarily unavailable, so I used the built-in help guide.`,
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  if (accountUiOpen) return null;

  return (
    <div className="km-assistant">
      {isOpen && (
        <section className="km-assistant-panel" aria-label="KalaManch Help Assistant">
          <div className="km-assistant-header">
            <div>
              <p className="km-assistant-eyebrow">KalaManch Help</p>
              <h2>Ask KalaManch</h2>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} aria-label="Close assistant">x</button>
          </div>

          <div className="km-assistant-messages">
            {messages.map((message, index) => (
              <article key={`${message.role}-${index}`} className={`km-assistant-message ${message.role}`}>
                {message.title && <h3>{message.title}</h3>}
                {message.body.split('\n').map((line, lineIndex) => (
                  <p key={`${message.role}-${index}-${lineIndex}`}>{line}</p>
                ))}
                {message.source && <span>{message.source}</span>}
              </article>
            ))}
            {isThinking && (
              <article className="km-assistant-message assistant">
                <h3>Ask KalaManch</h3>
                <p>Thinking...</p>
                <span>Live help</span>
              </article>
            )}
          </div>

          <div className="km-assistant-prompts">
            {suggestedPrompts.map((prompt) => (
              <button key={prompt} type="button" onClick={() => askAssistant(prompt)}>
                {prompt}
              </button>
            ))}
          </div>

          <form
            className="km-assistant-form"
            onSubmit={(event) => {
              event.preventDefault();
              askAssistant(query);
            }}
          >
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ask about joining, PMB, portfolio, verification..."
              aria-label="Ask KalaManch assistant"
            />
            <button type="submit">Ask</button>
          </form>

          <div className="km-assistant-footer">
            <button type="button" onClick={() => window.dispatchEvent(new Event('openArtistJoin'))}>Join Artist</button>
            <button type="button" onClick={() => window.dispatchEvent(new Event('openCollaboratorJoin'))}>Join Collaborator</button>
            {lastQuestion && (
              <Link to={buildSupportUrl(lastQuestion)} onClick={() => setIsOpen(false)}>Send this question to support</Link>
            )}
            <Link to="/faq" onClick={() => setIsOpen(false)}>Open FAQ</Link>
            <Link to="/contact" onClick={() => setIsOpen(false)}>Contact support</Link>
          </div>
        </section>
      )}

      <button type="button" className="km-assistant-toggle" onClick={() => setIsOpen((current) => !current)}>
        <span>Ask</span>
        <strong>KalaManch</strong>
      </button>
    </div>
  );
};

export default KalaManchAssistant;
